import axios from 'axios';

// Use absolute URL (with trailing slash stripped) when REACT_APP_BACKEND_URL is set (cross-origin
// or explicit origin deployments). Fall back to a same-origin relative path when the var is
// missing/empty, which works cleanly when the frontend and backend share one domain/proxy.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '') || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Create axios instance with credentials
const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth APIs
export const authAPI = {
  processSession: (sessionId, refCode = null) => {
    const params = new URLSearchParams({ session_id: sessionId });
    if (refCode) params.append('ref', refCode);
    return api.get(`/auth/session?${params.toString()}`);
  },
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  googleLogin: (data) => api.post('/auth/google-login', data),
};

// Business APIs
export const businessAPI = {
  create: (data) => api.post('/businesses', data),
  getAll: () => api.get('/businesses'),
  getOne: (id) => api.get(`/businesses/${id}`),
  update: (id, data) => api.put(`/businesses/${id}`, data),
  regenerateTemplates: (id) => api.post(`/businesses/${id}/regenerate-templates`),
  getCompetitorAnalysis: (id) => api.get(`/businesses/${id}/competitor-analysis`),
  getMyGmbProfile: (id) => api.get(`/businesses/${id}/my-gmb-profile`),
  deactivate: (id) => api.delete(`/businesses/${id}`),
  reactivate: (id) => api.post(`/businesses/${id}/reactivate`),
  deletePermanently: (id) => api.delete(`/businesses/${id}/permanent`),
};

// Dashboard APIs
export const dashboardAPI = {
  get: (businessId) => api.get(`/dashboard/${businessId}`),
};

// Campaign APIs
export const campaignAPI = {
  create: (businessId, data) => {
    return api.post(`/businesses/${businessId}/campaigns`, data);
  },
  getAll: (businessId) => api.get(`/businesses/${businessId}/campaigns`),
  getOne: (businessId, campaignId) => api.get(`/businesses/${businessId}/campaigns/${campaignId}`),
  update: (businessId, campaignId, data) => {
    return api.put(`/businesses/${businessId}/campaigns/${campaignId}`, data);
  },
  delete: (businessId, campaignId) => api.delete(`/businesses/${businessId}/campaigns/${campaignId}`),
  generateQR: (businessId, campaignId) => api.post(`/businesses/${businessId}/campaigns/${campaignId}/qr`),
  getAnalytics: (businessId, campaignId) => api.get(`/businesses/${businessId}/campaigns/${campaignId}/analytics`),
  generateMessage: (businessId, campaignType) => api.post(`/businesses/${businessId}/campaigns/generate-message`, { campaign_type: campaignType }),
};

// Public APIs (no auth required)
export const publicAPI = {
  getBusiness: (slug) => api.get(`/public/business/${slug}`),
  getReviewPage: (slug) => api.get(`/public/review/${slug}`),
  submitReview: (slug, data) => api.post(`/public/review/${slug}`, data),
  generateReviewText: (slug, data) => api.post(`/public/review/${slug}/generate-text`, data),
  getPlans: () => api.get('/plans'),
  getSiteBranding: () => api.get('/site-branding'),
  getCategories: () => api.get('/categories'),
  getTrendingBusinesses: (category, city) => api.get(`/trending/${encodeURIComponent(category)}/${encodeURIComponent(city)}`),
  getPage: (slug) => api.get(`/pages/${slug}`),
};

// Plans API
export const plansAPI = {
  getAll: () => api.get('/plans'),
};

// Subscription APIs
export const subscriptionAPI = {
  createOrder: (data) => api.post('/subscriptions/create-order', data),
  verifyPayment: (data) => api.post('/subscriptions/verify-payment', data),
  getHistory: (businessId) => api.get(`/subscriptions/${businessId}/history`),
};

// Admin APIs
export const adminAPI = {
  getBusinesses: () => api.get('/admin/businesses'),
  updateBusiness: (id, data) => api.put(`/admin/businesses/${id}`, data),
  deactivateBusiness: (id) => api.post(`/admin/businesses/${id}/deactivate`),
  reactivateBusiness: (id) => api.post(`/admin/businesses/${id}/reactivate`),
  deleteBusiness: (id) => api.delete(`/admin/businesses/${id}`),
  getUsers: () => api.get('/admin/users'),
  updateUserRole: (id, data) => api.put(`/admin/users/${id}/role`, data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  createStaff: (data) => api.post('/admin/staff', data),
  getStaff: () => api.get('/admin/staff'),
  getIncentives: () => api.get('/admin/incentives'),
  updateIncentive: (id, data) => api.put(`/admin/incentives/${id}`, data),
  // Field Boy Management
  createFieldBoy: (data) => api.post('/admin/field-boys', data),
  getFieldBoys: () => api.get('/admin/field-boys'),
  updateFieldBoy: (id, data) => api.put(`/admin/field-boys/${id}`, data),
  deleteFieldBoy: (id) => api.delete(`/admin/field-boys/${id}`),
  // Business Categories
  getCategories: () => api.get('/admin/categories'),
  createCategory: (data) => api.post('/admin/categories', data),
  updateCategory: (id, data) => api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/categories/${id}`),
  // API Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  // Site Settings (Logos & Pricing)
  getSiteSettings: () => api.get('/admin/site-settings'),
  updateSiteSettings: (data) => api.put('/admin/site-settings', data),
  // Static Pages
  getPages: () => api.get('/admin/pages'),
  createPage: (data) => api.post('/admin/pages', data),
  getPage: (id) => api.get(`/admin/pages/${id}`),
  updatePage: (id, data) => api.put(`/admin/pages/${id}`, data),
  deletePage: (id) => api.delete(`/admin/pages/${id}`),
  // CMS Sections
  getCMSSections: () => api.get('/admin/cms-sections'),
  createCMSSection: (data) => api.post('/admin/cms-sections', data),
  getCMSSection: (id) => api.get(`/admin/cms-sections/${id}`),
  updateCMSSection: (id, data) => api.put(`/admin/cms-sections/${id}`, data),
  deleteCMSSection: (id) => api.delete(`/admin/cms-sections/${id}`),
  // Dynamic QR Codes
  generateDynamicQRs: (data) => api.post('/admin/dynamic-qr/generate', data),
  getDynamicQRs: (status, batch) => api.get(`/admin/dynamic-qr${status || batch ? '?' : ''}${status ? `status=${status}` : ''}${status && batch ? '&' : ''}${batch ? `batch_name=${batch}` : ''}`),
  getDynamicQR: (id) => api.get(`/admin/dynamic-qr/${id}`),
  deleteDynamicQR: (id) => api.delete(`/admin/dynamic-qr/${id}`),
  // Notifications
  sendNotification: (data) => api.post('/admin/send-notification', data),
  // Financial Tracking
  getSubscriptionFinancials: () => api.get('/admin/subscriptions/financials'),
};

// CMS APIs (Public)
export const cmsAPI = {
  getSection: (key) => api.get(`/cms-sections/${key}`),
  getAllSections: () => api.get('/cms-sections'),
};

// Field Boy APIs (OTP Auth)
export const fieldAPI = {
  sendOtp: (phone) => api.post('/field/send-otp', { phone }),
  verifyOtp: (phone, otp) => api.post('/field/verify-otp', { phone, otp }),
  getMe: () => api.get('/field/me'),
  logout: () => api.post('/field/logout'),
  onboardBusiness: (data) => api.post('/field/onboard-business', data),
  getMyOnboardings: () => api.get('/field/my-onboardings'),
  getStats: () => api.get('/field/stats'),
};

// Reviews APIs
export const reviewsAPI = {
  getAll: (businessId, filter = 'all', rating = null) => {
    const params = new URLSearchParams({ filter });
    if (rating) params.append('rating', rating);
    return api.get(`/businesses/${businessId}/reviews?${params.toString()}`);
  },
  generateReply: (businessId, reviewId) => api.post(`/businesses/${businessId}/reviews/${reviewId}/generate-reply`),
  markReplied: (businessId, reviewId) => api.post(`/businesses/${businessId}/reviews/${reviewId}/mark-replied`),
};

// Team Members APIs (paid plans only)
export const teamAPI = {
  getAll: (businessId) => api.get(`/businesses/${businessId}/team`),
  add: (businessId, data) => api.post(`/businesses/${businessId}/team`, data),
  update: (businessId, memberId, data) => api.put(`/businesses/${businessId}/team/${memberId}`, data),
  remove: (businessId, memberId) => api.delete(`/businesses/${businessId}/team/${memberId}`),
};

// Referral APIs
export const referralAPI = {
  getMyLink: () => api.get('/referrals/my-link'),
  getStats: () => api.get('/referrals/stats'),
};

// GMB Rankings APIs (growth + premium plans)
export const rankingsAPI = {
  get: (businessId) => api.get(`/businesses/${businessId}/rankings`),
  runCheck: (businessId) => api.post(`/businesses/${businessId}/rankings/run-check`),
  exportCsv: (businessId) => api.get(`/businesses/${businessId}/rankings/export?fmt=csv`, { responseType: 'blob' }),
  exportPdf: (businessId) => api.get(`/businesses/${businessId}/rankings/export?fmt=pdf`, { responseType: 'blob' }),
};

// Geo-Grid Rank Heatmap APIs
export const geoGridAPI = {
  run: (businessId, keyword, gridSize = 5) =>
    api.post(`/businesses/${businessId}/rankings/geo-grid`, { keyword, grid_size: gridSize }),
  getLatest: (businessId, keyword, gridSize = 5) =>
    api.get(`/businesses/${businessId}/rankings/geo-grid`, { params: { keyword, grid_size: gridSize } }),
  getMapsApiKey: () => api.get('/config/maps-api-key'),
};

// Locations APIs (premium plan only)
export const locationsAPI = {
  getAll: (businessId) => api.get(`/businesses/${businessId}/locations`),
  add: (businessId, data) => api.post(`/businesses/${businessId}/locations`, data),
  remove: (businessId, locationId) => api.delete(`/businesses/${businessId}/locations/${locationId}`),
};

// GMB Keyword Purchase APIs
export const keywordPurchaseAPI = {
  getLimits: (businessId) => api.get(`/businesses/${businessId}/keyword-limits`),
  createOrder: (businessId, keyword) => api.post(`/businesses/${businessId}/keywords/purchase-order`, { keyword }),
  verifyPayment: (businessId, data) => api.post(`/businesses/${businessId}/keywords/purchase-verify`, data),
};

// Notifications APIs
export const notificationsAPI = {
  getAll: (businessId) => api.get(`/businesses/${businessId}/notifications`),
  markRead: (businessId, notificationId) => api.post(`/businesses/${businessId}/notifications/${notificationId}/read`),
};

// Staff Management APIs (staff review QR tracking)
export const staffAPI = {
  create: (businessId, data) => api.post(`/businesses/${businessId}/staff-profiles`, data),
  getAll: (businessId) => api.get(`/businesses/${businessId}/staff-profiles`),
  getPerformance: (businessId) => api.get(`/businesses/${businessId}/staff-performance`),
  delete: (businessId, staffId) => api.delete(`/businesses/${businessId}/staff-profiles/${staffId}`),
};

export default api;
